import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReimportCard } from "@/components/Reimport";
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Area, AreaChart, Legend } from "recharts";
import { Package, Truck, PoundSterling, Boxes, AlertTriangle, AlertOctagon, CheckCircle2, Clock } from "lucide-react";
import { api, fmtMoney, fmtNum, fmtDate, timeAgo } from "@/lib/api";
import { usePlant } from "@/context/PlantContext";
import { KpiCard, PageHeader, Panel, SCOPE, StatusChip } from "@/components/common";
import { SUP_STATUS } from "@/pages/Suppliers";
import { PlantSetupBanner } from "@/components/PlantSetupBanner";

const TT = { contentStyle: { borderRadius: 10, border: "1px solid #E2E8F0", fontSize: 12, boxShadow: "0 4px 16px rgba(15,23,42,.08)" } };

function NiTile() {
  const { params } = usePlant();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/ni/summary", { params }).then((r) => setS(r.data)).catch(() => setS(null)); }, [JSON.stringify(params)]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!s || !s.lines) return null;
  return (
    <button onClick={() => nav("/flows")} data-testid="ni-region-tile" className="mb-5 flex w-full items-center gap-3 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-left text-sm text-sky-900 transition-colors hover:bg-sky-100">
      <span className="rounded-md bg-sky-600 px-2 py-0.5 font-mono text-xs font-semibold text-white">XI</span>
      <span><b>Northern Ireland</b> · {fmtNum(s.customers)} customers · {fmtNum(s.lines)} sales lines · <b>{fmtNum(s.eudr_lines)}</b> GB→NI lines need a DDS (EUDR applies from 30 Dec 2026)</span>
    </button>
  );
}
const TYPE_LABELS = { vendors: "Vendors", customers: "Customers", po_lines: "PO lines", stock: "Stock", materials: "Materials", sales: "Sales" };

function UploadHealth({ d }) {
  return (
    <div className="space-y-2.5">
      {Object.keys(TYPE_LABELS).map((t) => {
        const u = d.latest_uploads[t];
        const stale = u && (Date.now() - new Date(u.created_at)) / 3.6e6 > d.overdue_hours;
        return (
          <div key={t} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2" data-testid={`upload-health-${t}`}>
            <span className="text-sm font-medium text-slate-700">{TYPE_LABELS[t]}</span>
            <span className="flex items-center gap-2 text-xs text-slate-500">
              {u ? timeAgo(u.created_at) : "no data yet"}
              {u ? <StatusChip status={stale ? "overdue" : u.status} label={stale ? "stale" : undefined} /> : <StatusChip status="queued" label="missing" />}
            </span>
          </div>
        );
      })}
      {d.sources.map((s) => (
        <div key={s.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2" data-testid={`dashboard-source-${s.id}`}>
          <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <Clock className="h-3.5 w-3.5 text-slate-400" /> {s.name}
          </span>
          <span className="flex items-center gap-2 text-xs text-slate-500">
            {timeAgo(s.last_seen_at)}
            <StatusChip status={s.status === "revoked" ? "revoked" : s.overdue ? "overdue" : "active"} label={s.status === "revoked" ? "revoked" : s.overdue ? "Overdue" : "on time"} testid={`dashboard-source-status-${s.id}`} />
          </span>
        </div>
      ))}
    </div>
  );
}

function Charts({ d }) {
  const nav = useNavigate();
  const donut = d.scope_breakdown.filter((x) => x.count > 0);
  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <Panel title="In-scope materials by commodity" subtitle="Material × plant records, incl. items needing check" className="lg:col-span-2" testid="chart-by-commodity">
        <div className="h-64">
          <ResponsiveContainer initialDimension={{ width: 600, height: 240 }}>
            <BarChart data={d.by_commodity} layout="vertical" margin={{ left: 10, right: 20 }}>
              <CartesianGrid horizontal={false} stroke="#F1F5F9" />
              <XAxis type="number" tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis type="category" dataKey="commodity" width={80} tick={{ fontSize: 12, fill: "#334155" }} />
              <Tooltip {...TT} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="in_scope" name="In scope" stackId="a" fill="#059669" radius={[0, 0, 0, 0]} />
              <Bar dataKey="check" name="Check" stackId="a" fill="#F59E0B" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Panel>
      <Panel title="Scope breakdown" subtitle="All material × plant records" testid="chart-scope-donut">
        <div className="relative h-64">
          <ResponsiveContainer initialDimension={{ width: 600, height: 240 }}>
            <PieChart>
              <Pie data={donut} dataKey="count" nameKey="scope" innerRadius={62} outerRadius={92} paddingAngle={2} onClick={(e) => nav(`/materials?scope=${e.scope}`)}>
                {donut.map((x) => (
                  <Cell key={x.scope} fill={SCOPE[x.scope].color} className="cursor-pointer" />
                ))}
              </Pie>
              <Tooltip {...TT} formatter={(v, n) => [fmtNum(v), SCOPE[n]?.label]} />
            </PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <span className="font-display text-2xl font-semibold text-slate-900">{fmtNum(d.kpis.materials_total)}</span>
            <span className="text-[11px] uppercase tracking-wider text-slate-500">records</span>
          </div>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-1.5">
          {d.scope_breakdown.map((x) => (
            <span key={x.scope} className="flex items-center gap-1.5 text-xs text-slate-600">
              <span className="h-2 w-2 rounded-full" style={{ background: SCOPE[x.scope].color }} /> {SCOPE[x.scope].label} · {fmtNum(x.count)}
            </span>
          ))}
        </div>
      </Panel>
      <Panel title="Scope by plant" subtitle="Click a bar to open the plant's materials" className="lg:col-span-2" testid="chart-by-plant">
        <div className="h-64">
          <ResponsiveContainer initialDimension={{ width: 600, height: 240 }}>
            <BarChart data={d.by_plant} onClick={(e) => e?.activeLabel && nav(`/materials?plant=${e.activeLabel}`)}>
              <CartesianGrid vertical={false} stroke="#F1F5F9" />
              <XAxis dataKey="plant" tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
              <Tooltip {...TT} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {["in_scope", "check", "missing_code", "out_of_scope"].map((k, i) => (
                <Bar key={k} dataKey={k} name={SCOPE[k].label} stackId="p" fill={SCOPE[k].color} radius={i === 3 ? [4, 4, 0, 0] : 0} className="cursor-pointer" />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Panel>
      <Panel title="In-scope vendors by country" subtitle="Vendors with PO lines for in-scope materials" testid="chart-vendor-country">
        <div className="h-64">
          <ResponsiveContainer initialDimension={{ width: 600, height: 240 }}>
            <BarChart data={d.vendor_countries} layout="vertical" margin={{ left: 0, right: 16 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="country" width={36} interval={0} tick={{ fontSize: 12, fill: "#334155" }} />
              <Tooltip {...TT} formatter={(v, n) => (n === "spend_gbp" ? [fmtMoney(v), "Spend 12m"] : [v, "Vendors"])} />
              <Bar dataKey="vendors" fill="#1C1C1E" radius={[0, 4, 4, 0]} barSize={14} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Panel>
      <Panel title="Upload health — last 30 days" subtitle="Rows processed and failed per day" className="lg:col-span-2" testid="chart-upload-timeline">
        <div className="h-56">
          <ResponsiveContainer initialDimension={{ width: 600, height: 240 }}>
            <AreaChart data={d.upload_timeline}>
              <defs>
                <linearGradient id="gRows" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#059669" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#059669" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="#F1F5F9" />
              <XAxis dataKey="date" tickFormatter={(v) => fmtDate(v).slice(0, 6)} tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
              <Tooltip {...TT} labelFormatter={(v) => fmtDate(v)} />
              <Area type="monotone" dataKey="rows" name="Rows processed" stroke="#059669" fill="url(#gRows)" strokeWidth={2} />
              <Area type="monotone" dataKey="failed_rows" name="Failed rows" stroke="#E11D48" fill="#E11D48" fillOpacity={0.08} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Panel>
      <Panel title="Supplier pipeline" subtitle={`${d.suppliers.total} EUDR suppliers · ${d.suppliers.completion_pct}% approved · ${d.suppliers.submitted_pct}% submitted or approved`} className="lg:col-span-2" testid="chart-supplier-pipeline">
        <div className="h-56">
          <ResponsiveContainer initialDimension={{ width: 600, height: 240 }}>
            <BarChart data={d.suppliers.pipeline.map((p) => ({ ...p, label: SUP_STATUS[p.status][0] }))} onClick={(e) => e?.activePayload && nav(`/suppliers?status=${e.activePayload[0].payload.status}`)}>
              <CartesianGrid vertical={false} stroke="#F1F5F9" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} interval={0} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748B" }} />
              <Tooltip {...TT} />
              <Bar dataKey="count" name="Suppliers" radius={[4, 4, 0, 0]} className="cursor-pointer">
                {d.suppliers.pipeline.map((p) => <Cell key={p.status} fill={SUP_STATUS[p.status][2]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Panel>
      <Panel title="Supplier completion" testid="supplier-completion">
        <p className="font-display text-5xl font-semibold text-slate-900" data-testid="supplier-completion-pct">{d.suppliers.completion_pct}%</p>
        <p className="mt-1 text-sm text-slate-500">of EUDR suppliers approved</p>
        <div className="mt-4 h-2.5 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-emerald-600" style={{ width: `${d.suppliers.completion_pct}%` }} /></div>
        <p className="mt-4 text-sm text-slate-600">{d.suppliers.submitted_pct}% submitted or approved</p>
      </Panel>
      <Panel title="Feeds & scripts" subtitle={`Overdue after ${d.overdue_hours}h without upload`} testid="upload-health-panel">
        <UploadHealth d={d} />
      </Panel>
    </div>
  );
}

export default function DetailedDashboard() {
  const { params, label } = usePlant();
  const nav = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => {
    setD(null);
    api.get("/dashboard", { params }).then((r) => setD(r.data));
  }, [params]);

  const k = d?.kpis;
  const stock = k?.in_scope_stock_value || {};
  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Detailed dashboard" subtitle={`EUDR scope position for ${label}. Data from the latest SAP extracts.`} testid="dashboard-header">
        <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600" data-testid="dashboard-plant-label">
          {label}
        </span>
      </PageHeader>
      <PlantSetupBanner />
      <div className="mb-5"><ReimportCard params={params} testid="dashboard-reimport" /></div>
      <NiTile />
      {d && k.overdue_sources > 0 && (
        <div className="mb-5 flex items-center gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 fade-up" data-testid="overdue-alert">
          <AlertOctagon className="h-4 w-4" />
          {k.overdue_sources} script source{k.overdue_sources > 1 ? "s are" : " is"} overdue (no upload for more than {d.overdue_hours} hours).
          <button className="ml-auto font-semibold underline-offset-2 hover:underline" onClick={() => nav("/scripts")}>
            Review scripts
          </button>
        </div>
      )}
      <div className="stagger grid gap-4 sm:grid-cols-2 xl:grid-cols-6">
        <KpiCard testid="kpi-materials-in-scope" label="Materials in scope" icon={CheckCircle2} tone="green" value={d ? fmtNum(k.materials_in_scope) : "…"}
          sub={d && `${fmtNum(k.distinct_in_scope_materials)} distinct materials · ${k.materials_total ? Math.round((100 * k.materials_in_scope) / k.materials_total) : 0}% of records`}
          onClick={() => nav("/materials?scope=in_scope")} />
        <KpiCard testid="kpi-in-scope-in-stock" label="In scope in stock" icon={Boxes} tone="green" value={d ? fmtNum(k.in_scope_in_stock?.in_scope) : "…"}
          sub={d && `${k.in_scope_in_stock?.check ? `+${fmtNum(k.in_scope_in_stock.check)} Check · ` : ""}qty ${fmtNum(k.in_scope_in_stock?.qty)} · £${fmtNum(Math.round(k.in_scope_in_stock?.value_gbp || 0))}${k.in_scope_in_stock?.converted ? " (converted)" : ""}`}
          onClick={() => nav(`/materials?scope=in_scope&stock=in${params.plant ? `&plant=${params.plant}` : ""}`)} />
        <KpiCard testid="kpi-in-scope-vendors" label="In-scope vendors" icon={Truck} tone="slate" value={d ? fmtNum(k.in_scope_vendors) : "…"}
          sub="Supplying in-scope materials (PO lines)" onClick={() => nav("/data/vendors?in_scope=true")} />
        <KpiCard testid="kpi-in-scope-spend" label="In-scope PO spend · 12m" icon={PoundSterling} tone="brand" value={d ? fmtMoney(k.in_scope_po_spend_gbp_12m, "GBP", true) : "…"}
          sub={d && `${fmtNum(k.in_scope_po_lines_12m)} PO lines, GBP`} onClick={() => nav("/data/po_lines?in_scope=true")} />
        <KpiCard testid="kpi-in-scope-stock" label="In-scope stock value" icon={Boxes} tone="slate" value={d ? fmtMoney(stock.GBP || 0, "GBP", true) : "…"}
          sub={d && (Object.entries(stock).filter(([c]) => c !== "GBP").map(([c, v]) => `+ ${fmtMoney(v, c, true)}`).join(" ") || "All currencies")}
          onClick={() => nav("/data/stock?in_scope=true")} />
        <KpiCard testid="kpi-needs-check" label="Needs check" icon={AlertTriangle} tone="amber" value={d ? fmtNum(k.needs_check + k.missing_code) : "…"}
          sub={d && `${fmtNum(k.needs_check)} partial headings · ${fmtNum(k.missing_code)} missing code`} onClick={() => nav("/materials?scope=check,missing_code")} />
      </div>
      <div className="mt-6">{d ? <Charts d={d} /> : <div className="h-96 animate-pulse rounded-xl bg-slate-100" />}</div>
      {d && k.materials_total === 0 && (
        <div className="mt-6 flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-5 text-sm text-slate-600">
          <Package className="h-5 w-5 text-slate-400" /> No materials yet. Upload the SAP “Commodity Codes & Stock” extract to see scope.
          <button onClick={() => nav("/uploads")} className="ml-auto rounded-lg bg-[#D52B3B] px-3 py-1.5 font-medium text-white hover:bg-[#661D1D]">Go to uploads</button>
        </div>
      )}
    </div>
  );
}
